import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import { useIntl } from '../../../util/reactIntl';
import { hideTidioChat, showTidioChat } from '../../../util/tidioChat';
import {
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  showFullscreenButton: false,
  disableThumbnailScroll: true,
};

const getVariantUrlMaybe = (image, variantName) => {
  const v = image?.attributes?.variants?.[variantName];
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return v.url || null;
  return null;
};

const pickFirstVariantUrl = (image, variantNames) => {
  if (!Array.isArray(variantNames) || variantNames.length === 0) return '';
  return variantNames.map(v => getVariantUrlMaybe(image, v)).find(Boolean) || '';
};

// ─── Lightbox ────────────────────────────────────────────────────────────────

const ImageLightbox = ({ images, imageVariants, startIndex, onClose, intl }) => {
  const [index, setIndex] = useState(startIndex);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    hideTidioChat();
    return () => showTidioChat();
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setIndex(i => (i + 1) % images.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [images.length, onClose]);

  const currentImage = images[index];
  const total = images.length;
  const hasMultiple = total > 1;

  const closeLabel = intl.formatMessage({ id: 'ListingImageGallery.closeModalTitle' });

  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) onClose();
  };

  const goLeft = e => {
    e.stopPropagation();
    setIndex(i => (i - 1 + total) % total);
  };
  const goRight = e => {
    e.stopPropagation();
    setIndex(i => (i + 1) % total);
  };

  const imageAlt = intl.formatMessage(
    { id: 'ListingImageGallery.imageAltText' },
    { index: index + 1, count: total }
  );

  return ReactDOM.createPortal(
    <div className={css.lightboxOverlay} onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <button className={css.lightboxClose} onClick={onClose} title={closeLabel} aria-label={closeLabel}>
        <IconClose />
      </button>

      <div className={css.lightboxInner} onClick={e => e.stopPropagation()}>
        <ResponsiveImage
          rootClassName={css.lightboxImage}
          image={currentImage}
          alt={imageAlt}
          variants={imageVariants}
          sizes="(max-width: 768px) 95vw, 90vw"
        />
      </div>

      {hasMultiple && (
        <button className={css.lightboxNavLeft} onClick={goLeft} aria-label="Previous image">
          <div className={css.lightboxNavArrow}>
            <IconArrowHead direction="left" size="big" />
          </div>
        </button>
      )}
      {hasMultiple && (
        <button className={css.lightboxNavRight} onClick={goRight} aria-label="Next image">
          <div className={css.lightboxNavArrow}>
            <IconArrowHead direction="right" size="big" />
          </div>
        </button>
      )}

      {hasMultiple && (
        <div className={css.lightboxCounter}>
          {index + 1} / {total}
        </div>
      )}
    </div>,
    document.body
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * The ListingImageGallery component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {Array<propTypes.image>} props.images - The images
 * @param {Array<string>} props.imageVariants - The image variants
 * @param {Array<string>} props.thumbnailVariants - The thumbnail variants
 * @returns {JSX.Element} listing image gallery component
 */
const ListingImageGallery = props => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const wrapperRef = useRef(null);
  const intl = useIntl();
  const { rootClassName, className, images, imageVariants, thumbnailVariants } = props;
  const thumbVariants = thumbnailVariants || imageVariants;

  const items = images.map((img, i) => {
    const originalAlt = intl.formatMessage(
      { id: 'ListingImageGallery.imageAltText' },
      { index: i + 1, count: images.length }
    );
    const thumbnailAlt = intl.formatMessage(
      { id: 'ListingImageGallery.imageThumbnailAltText' },
      { index: i + 1, count: images.length }
    );
    return {
      original: pickFirstVariantUrl(img, imageVariants),
      thumbnail: pickFirstVariantUrl(img, thumbVariants),
      originalAlt,
      thumbnailAlt,
      alt: originalAlt,
      thumbAlt: thumbnailAlt,
      image: img,
    };
  });

  const handleImageClick = () => {
    setLightboxIndex(currentIndex);
    setIsLightboxOpen(true);
  };

  const handleLightboxClose = useCallback(() => {
    setIsLightboxOpen(false);
  }, []);

  const imageSizes = { sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px` };

  const renderItem = item => {
    return (
      <div className={css.itemContainer} onClick={handleImageClick} style={{ cursor: 'pointer' }}>
        <ResponsiveImage
          rootClassName={css.item}
          image={item.image}
          alt={item.alt}
          variants={imageVariants}
          {...imageSizes}
        />
      </div>
    );
  };

  const renderThumbInner = item => {
    return (
      <div>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const scrollThumbnailIndexIntoView = useCallback(
    index => {
      const root = wrapperRef.current;
      if (!root) return;

      const thumbs = root.querySelector('.image-gallery-thumbnails');
      if (!thumbs) return;

      const allThumbs = root.querySelectorAll('.image-gallery-thumbnail');
      const target = allThumbs?.[index];
      if (!target) return;

      const cRect = thumbs.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();
      const pad = 16;

      const isHorizontal = thumbs.scrollWidth > thumbs.clientWidth + 1;
      if (isHorizontal) {
        const leftOverflow = tRect.left - cRect.left;
        const rightOverflow = tRect.right - cRect.right;
        if (leftOverflow < pad) {
          thumbs.scrollLeft += leftOverflow - pad;
        } else if (rightOverflow > -pad) {
          thumbs.scrollLeft += rightOverflow + pad;
        }
      } else {
        const topOverflow = tRect.top - cRect.top;
        const bottomOverflow = tRect.bottom - cRect.bottom;
        if (topOverflow < pad) {
          thumbs.scrollTop += topOverflow - pad;
        } else if (bottomOverflow > -pad) {
          thumbs.scrollTop += bottomOverflow + pad;
        }
      }
    },
    []
  );

  const syncActiveThumbnailScroll = useCallback(() => {
    const root = wrapperRef.current;
    if (!root) return;

    const thumbs = root.querySelector('.image-gallery-thumbnails');
    const activeThumb = root.querySelector('.image-gallery-thumbnail.active');
    if (!thumbs || !activeThumb) return;

    const cRect = thumbs.getBoundingClientRect();
    const tRect = activeThumb.getBoundingClientRect();
    const pad = 16;

    const isHorizontal = thumbs.scrollWidth > thumbs.clientWidth + 1;
    if (isHorizontal) {
      const leftOverflow = tRect.left - cRect.left;
      const rightOverflow = tRect.right - cRect.right;
      if (leftOverflow < pad) {
        thumbs.scrollLeft += leftOverflow - pad;
      } else if (rightOverflow > -pad) {
        thumbs.scrollLeft += rightOverflow + pad;
      }
    } else {
      const topOverflow = tRect.top - cRect.top;
      const bottomOverflow = tRect.bottom - cRect.bottom;
      if (topOverflow < pad) {
        thumbs.scrollTop += topOverflow - pad;
      } else if (bottomOverflow > -pad) {
        thumbs.scrollTop += bottomOverflow + pad;
      }
    }
  }, []);

  const onSlide = index => {
    setCurrentIndex(index);
    requestAnimationFrame(syncActiveThumbnailScroll);
  };

  useEffect(() => {
    syncActiveThumbnailScroll();
  }, [currentIndex, syncActiveThumbnailScroll]);

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button
        className={css.navLeft}
        disabled={disabled}
        type="button"
        onClick={e => {
          if (!disabled) {
            const prevIndex = (currentIndex - 1 + items.length) % items.length;
            scrollThumbnailIndexIntoView(prevIndex);
          }
          onClick(e);
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };

  const renderRightNav = (onClick, disabled) => {
    return (
      <button
        className={css.navRight}
        disabled={disabled}
        type="button"
        onClick={e => {
          if (!disabled) {
            const nextIndex = (currentIndex + 1) % items.length;
            scrollThumbnailIndexIntoView(nextIndex);
          }
          onClick(e);
        }}
      >
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };

  if (items.length === 0) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div ref={wrapperRef} className={css.galleryWrapper}>
      <ReactImageGallery
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        onSlide={onSlide}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        {...IMAGE_GALLERY_OPTIONS}
      />

      {/* Image counter - visible only on mobile */}
      {items.length > 1 && (
        <div className={css.mobileImageCounter}>
          {currentIndex + 1}/{items.length}
        </div>
      )}

      {isLightboxOpen && (
        <ImageLightbox
          images={images}
          imageVariants={imageVariants}
          startIndex={lightboxIndex}
          onClose={handleLightboxClose}
          intl={intl}
        />
      )}
    </div>
  );
};

export default ListingImageGallery;
